import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import type { Permission } from '@aeris/permissions'

import { fetchCompany } from './api.ts'
import { useAuth } from './auth-context.ts'
import { MODULES, type ModuleEntry } from './modules.tsx'

/**
 * Quais telas esta pessoa vê — uma regra só (F138).
 *
 * ## Por que existe
 *
 * Eram três cópias do mesmo filtro — a barra lateral, o Ctrl+K e a grade do
 * Início —, e elas já discordavam: só a barra lateral sabia que a loja que não
 * conta estoque não tem Estoque. O Modo PC traz mais dois lugares que listam
 * telas, a barra de menus e a barra de ícones; com cinco cópias, a pergunta
 * "por que aparece aqui e não ali?" deixaria de ter resposta.
 *
 * ## As perguntas, na ordem
 *
 * - `hidden`: a tela existe e não ocupa linha nenhuma.
 * - `inTab`: é aba de outra página (F068). Sai da barra lateral e **continua**
 *   na busca, no Início e nos menus — `includeTabs` diz qual dos dois é.
 * - `permission` e `anyOf`: o que o papel concede.
 * - Estoque só para quem conta estoque (F102).
 * - `feature`: a chave que o console da plataforma ligou nesta conta (F169).
 * - `showsPage`: o que o administrador deixou marcado para esta conta.
 *
 * Nenhuma delas é a proteção: a API recusa por conta própria.
 */

export type VisibilityRules = {
  readonly allows: (permission: Permission) => boolean
  readonly showsPage: (module: string) => boolean
  /** Falso só quando a empresa diz, com todas as letras, que não conta. */
  readonly countsStock: boolean
  /**
   * A chave está ligada nesta conta? (F169)
   *
   * **Segunda trava, e não a primeira.** O servidor já tira de `session.pages`
   * a tela de chave desligada, e `showsPage` abaixo bastaria — exceto por uma
   * porta: `showsPage` devolve `true` quando `pages` está VAZIO, que é como
   * chega uma sessão guardada por uma versão anterior do produto. Sem esta
   * linha, seria exatamente nessas sessões que a tela de outro cliente
   * apareceria.
   */
  readonly hasFeature: (feature: string) => boolean
  /** Verdadeiro em todo lugar menos na barra lateral. */
  readonly includeTabs: boolean
}

export const visibleModules = (
  modules: readonly ModuleEntry[],
  rules: VisibilityRules
): readonly ModuleEntry[] =>
  modules.filter(
    (item) =>
      item.hidden !== true &&
      (rules.includeTabs || item.inTab === undefined) &&
      (item.permission === undefined || rules.allows(item.permission)) &&
      (item.anyOf === undefined ||
        item.anyOf.some((permission) => rules.allows(permission))) &&
      (item.module !== 'estoque' || rules.countsStock) &&
      (item.feature === undefined || rules.hasFeature(item.feature)) &&
      rules.showsPage(item.module)
  )

export const useVisibleModules = ({
  includeTabs,
}: {
  includeTabs: boolean
}): readonly ModuleEntry[] => {
  const { session, allows, showsPage, hasFeature } = useAuth()

  /*
   * A loja que não conta estoque não tem Estoque (F102).
   *
   * Decisão do dono, e a frase dele é a justificativa: *"se desligar de verdade
   * some"*. Uma tela que existe para um número que ninguém mantém é uma tela
   * que mente — e a rota continua respondendo, então nada foi apagado: religar
   * a chave traz a linha e o histórico de volta.
   *
   * Mesma `queryKey` de Produtos e do Início, então o cache é um só e isto não
   * custa uma requisição a mais. Enquanto carrega, a linha aparece: sumir e
   * reaparecer é pior do que demorar a sumir.
   */
  const companyId = session?.context?.companyId ?? null
  const company = useQuery({
    queryKey: ['company', companyId],
    queryFn: ({ signal }) => fetchCompany(companyId ?? '', signal),
    enabled: companyId !== null && allows('organization.read'),
  })

  const countsStock = company.data?.company.salesFlow.countsStock !== false

  return useMemo(
    () =>
      visibleModules(MODULES, {
        allows,
        showsPage,
        hasFeature,
        countsStock,
        includeTabs,
      }),
    [allows, showsPage, hasFeature, countsStock, includeTabs]
  )
}
